import React, { useState, useEffect, useRef } from 'react';
import { Bell } from '@phosphor-icons/react';
import { getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

export default function NotificationBell() {
  const { isSpanish } = useLanguage();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const { data } = await getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (e) {}
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await getNotifications({ limit: 20 });
      setNotifications(data.notifications);
    } catch (e) {} finally { setLoading(false); }
  };

  const handleToggle = () => {
    if (!isOpen) fetchNotifications();
    setIsOpen(!isOpen);
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch (e) {}
  };

  const typeIcon = (type) => {
    const map = { success: 'text-emerald-400', error: 'text-red-400', warning: 'text-yellow-400', approval: 'text-blue-400', reminder: 'text-purple-400' };
    return map[type] || 'text-[#9CA3AF]';
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={handleToggle} className="relative p-2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors">
        <Bell className="w-5 h-5" weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-10 w-80 max-h-96 bg-[#151B26] border border-[#242E42] rounded-lg shadow-2xl z-50 overflow-hidden">
          <div className="p-3 border-b border-[#242E42] flex justify-between items-center">
            <span className="text-sm font-semibold text-[#F3F4F6]">
              {isSpanish ? 'Notificaciones' : 'Notifications'}
            </span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-[#E2B05E] hover:text-[#F0C47A]">
                {isSpanish ? 'Marcar todas leidas' : 'Mark all read'}
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-72">
            {loading ? (
              <div className="p-4 text-center text-[#9CA3AF] text-sm">{isSpanish ? 'Cargando...' : 'Loading...'}</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-[#9CA3AF] text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {isSpanish ? 'Sin notificaciones' : 'No notifications'}
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n._id}
                  onClick={() => !n.read && handleMarkRead(n._id)}
                  className={`p-3 border-b border-[#242E42]/50 cursor-pointer hover:bg-[#1E2636]/50 transition-colors ${!n.read ? 'bg-[#1E2636]/30' : ''}`}
                >
                  <div className="flex gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-[#E2B05E]' : 'bg-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${!n.read ? 'text-[#F3F4F6]' : 'text-[#9CA3AF]'}`}>{n.title}</p>
                      <p className="text-xs text-[#9CA3AF] mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-[#6B7280] mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString('es-ES') : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
